import { AgentService } from '@/services';
import { IHenkaWeeklyRun } from '@/types/response';
import {
  Alert,
  Button,
  Card,
  Divider,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import { Helmet } from 'react-helmet-async';
import { useQuery } from 'react-query';

const { Title, Paragraph, Text } = Typography;

interface IProps {
  title?: string;
}

/** 指標キー → 表示名。集約層の HenkaMetricKey と対応する */
const METRIC_LABEL: Record<string, string> = {
  steps: '歩数',
  brake: '急ブレーキ（距離あたり）',
  accel: '急アクセル（距離あたり）',
  dscore: '運転スコア',
  orientation: '見当識',
  memory: '記憶力',
  planning: '計画力',
  spatial: '空間認識力',
  attention: '注意力',
  familyActivity: '家族の活動量',
};

/** decision → 表示名と色 */
const DECISION_TAG: Record<string, { label: string; color: string }> = {
  steps_up: { label: '歩数が増えた', color: 'green' },
  steps_down: { label: '歩数が減った', color: 'orange' },
  drive: { label: '運転が変わった', color: 'volcano' },
  drive_up: { label: '運転が良くなった', color: 'blue' },
  cognitive: { label: '声かけのきっかけ', color: 'purple' },
  activity_up: { label: 'やりとりが増えた', color: 'green' },
  activity_down: { label: 'やりとりが減った', color: 'orange' },
  none: { label: '変化なし', color: 'default' },
};

const label = (key: string) => METRIC_LABEL[key] ?? key;

/**
 * 取得失敗の表示。
 *
 * この画面は「0件・空が正常」と明記しているので、**失敗を黙って0件として出すと
 * 障害が正常に見える**。数字が出ないときは「0」ではなく「わからない」と言う。
 */
const LoadError: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <Alert
    type='error'
    showIcon
    message='取得できませんでした'
    description={
      <Space direction='vertical' size={4}>
        <Text>
          これは「0件」ではなく<Text strong>不明</Text>
          です。APIが落ちている可能性があるので、数字を読まないでください。
        </Text>
        <Button size='small' onClick={onRetry}>
          再読み込み
        </Button>
      </Space>
    }
  />
);

const countsToTags = (counts: Record<string, number> | undefined) => {
  const entries = Object.entries(counts ?? {}).filter(([, n]) => n > 0);
  if (!entries.length) return <Text type='secondary'>—</Text>;

  return (
    <Space size={[4, 4]} wrap>
      {entries.map(([key, n]) => {
        const tag = DECISION_TAG[key];

        return (
          <Tag key={key} color={tag?.color}>
            {(tag?.label ?? key) + ' ' + n}
          </Tag>
        );
      })}
    </Space>
  );
};

export const AgentHenkaCoverage: React.FC<IProps> = ({ title }) => {
  const {
    data: coverage,
    isLoading: coverageLoading,
    isError: coverageError,
    refetch: refetchCoverage,
  } = useQuery(['henka-coverage'], () => AgentService.getHenkaCoverage());
  const {
    data: runs,
    isLoading: runsLoading,
    isError: runsError,
    refetch: refetchRuns,
  } = useQuery(['henka-runs'], () => AgentService.listHenkaRuns(12));

  const c = coverage?.data;
  const runRows = runs?.data ?? [];

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <Title level={4}>変化のたより（henka）</Title>

      <Alert
        type='info'
        showIcon
        style={{ marginBottom: 16 }}
        message='便りが少ないのは異常ではありません'
        description={
          <Space direction='vertical' size={6}>
            <Text>
              便りが届くのは、次の<Text strong>3つをすべて満たした家族</Text>
              だけです。
            </Text>
            <Text>
              ①<Text strong>データがそろっている</Text>
              （30歳以上の誰かに歩数や運転などが数週間分ある）
              {' ＋ '}②<Text strong>家族が使っている</Text>
              （2人以上いて、最近アプリを開いている）
              {' ＋ '}③<Text strong>変化が見つかった</Text>
            </Text>
            <Text>
              ①と②は<Text strong>別の条件</Text>
              なので、片方だけ多いことがあります。数字が少ないとき、①が少なければ
              データ不足、②が少なければ家族が使っていない、③が少なければ単に変化が
              なかっただけで、<Text strong>いずれも故障ではありません</Text>。
              ③が0件の週も普通にあります。
            </Text>
          </Space>
        }
      />

      <Card
        loading={coverageLoading}
        title='①② 便りが出うる家族'
        style={{ marginBottom: 16 }}
        extra={
          !coverageError &&
          c && (
            <Text type='secondary'>
              {`30歳以上のメンバーについて、直近${c.window.weeks}週（${c.window.from} 〜 ${c.window.to}）のうち${c.window.minPoints}週分以上のデータがある家族`}
            </Text>
          )
        }
      >
        {coverageError ? (
          <LoadError onRetry={() => void refetchCoverage()} />
        ) : (
          <>
            {/* Col で分けると幅いっぱいに広がって間延びするので、Space で詰める */}
            <Space size={40} align='start' wrap style={{ marginBottom: 12 }}>
              <Statistic
                title='① データがそろっている'
                value={c?.eligibleFamilies ?? 0}
                suffix={`/ ${c?.totalFamilies ?? 0}`}
              />
              <Statistic
                title='② 家族が使っている'
                value={c?.batchTargetFamilies ?? 0}
                suffix={`/ ${c?.totalFamilies ?? 0}`}
              />
              <Divider type='vertical' style={{ height: 52, margin: 0 }} />
              <Statistic
                title='①かつ② = 便りが出うる'
                value={c?.batchTargetWithData ?? 0}
                valueStyle={{ fontWeight: 600 }}
              />
            </Space>

            <Paragraph type='secondary' style={{ marginBottom: 16 }}>
              ①は<Text strong>30歳以上のメンバー</Text>
              の歩数・運転・脳トレ、または家族の活動量のうち
              <Text strong>どれか1つでもデータが足りている</Text>
              家族（共有オフのデータは過去分も除いています）。
              毎週のチェックは30歳以上を全員見て、変化が見つかった中から
              1人について1通だけ書きます。生年月日が未登録の人は対象外です。②は
              <Text strong>2人以上いて、直近1週間に利用がある</Text>
              家族で、毎週のチェックが実際に見にいく範囲です。
              <Text strong>
                下の「変化が見つかった家族」の「調べた家族」は②と一致します。
              </Text>
              ①にいても②に入らない家族は、データはあっても今は使われていないため
              便りの対象になりません。
            </Paragraph>

            <Table
              size='small'
              rowKey='metricKey'
              pagination={false}
              dataSource={c?.metrics ?? []}
              columns={[
                {
                  title: '指標',
                  dataIndex: 'metricKey',
                  render: (key: string) => label(key),
                },
                {
                  title: '成立している系列',
                  dataIndex: 'series',
                  align: 'right',
                  render: (n: number) => n.toLocaleString(),
                },
                {
                  title: '家族数',
                  dataIndex: 'families',
                  align: 'right',
                  render: (n: number) => n.toLocaleString(),
                },
              ]}
              locale={{ emptyText: 'データが足りている家族がまだいません' }}
            />
          </>
        )}
      </Card>

      <Card
        loading={runsLoading}
        title='③ 変化が見つかった家族（直近12週）'
        extra={
          <Text type='secondary'>
            「先週も同じ便り」が多いほど、同じ家族に毎週届きやすい＝間隔を空ける必要がある
          </Text>
        }
      >
        {runsError ? (
          <LoadError onRetry={() => void refetchRuns()} />
        ) : (
          <Table<IHenkaWeeklyRun>
            size='small'
            rowKey='id'
            pagination={false}
            dataSource={runRows}
            columns={[
              { title: '週（月曜）', dataIndex: 'weekStart', width: 120 },
              {
                title: '判定対象',
                dataIndex: 'eligible',
                align: 'right',
                width: 100,
                render: (n: number) => n.toLocaleString(),
              },
              {
                title: '見つかった変化の内訳',
                dataIndex: 'decisions',
                render: countsToTags,
              },
              {
                title: '先週も同じ便り',
                dataIndex: 'repeatedFromPrevWeek',
                align: 'right',
                width: 110,
                // 前週の記録が無い週は「0件」ではなく「比べられない」。
                // ここを 0 と書くとクールダウンが不要だと誤読される。
                render: (n: number | null) =>
                  n === null ? <Text type='secondary'>—</Text> : n,
              },
              {
                title: '便りを送った数',
                dataIndex: 'posted',
                align: 'right',
                width: 80,
              },
              {
                title: '送らなかった理由',
                dataIndex: 'skipped',
                render: countsToTags,
              },
            ]}
            locale={{
              emptyText:
                '毎週の自動チェックがまだ動いていないため空です（次の開発で動き始めます）',
            }}
          />
        )}
      </Card>
    </>
  );
};
